/**
 * StageProgressStrip — full-width pipeline visual.
 *
 * Vercel/Linear pipeline-style horizontal stepper. Solid brand-purple line
 * for completed + active; dashed muted line for upcoming. Numbered circles
 * with a check icon on completed steps.
 *
 * In Edit mode (`canEdit && onChange`), each step becomes clickable to advance
 * the stage. Outside edit mode, it's a read-only timeline.
 */
import { useTranslation } from 'react-i18next';
import { Check } from 'lucide-react';
import { cn } from '@/lib/utils';
import { PRODUCT_DEV_PIPELINE_STAGES } from '../utils/status-style';

interface Props {
  currentStage: string;
  canEdit?: boolean;
  onChange?: (next: string) => void;
  /** The chain to render — defaults to the garment pipeline. Pass the task
   *  chain (open → in_progress → done) for record_type 'task'. */
  stages?: readonly string[];
}

export function StageProgressStrip({
  currentStage,
  canEdit,
  onChange,
  stages = PRODUCT_DEV_PIPELINE_STAGES,
}: Props) {
  const { t } = useTranslation('productDev');
  const currentIdx = Math.max(0, stages.indexOf(currentStage));

  return (
    <div className="w-full py-4">
      <div className="flex items-start gap-0">
        {stages.map((stage, idx) => {
          const isComplete = idx < currentIdx;
          const isActive = idx === currentIdx;
          const isLast = idx === stages.length - 1;
          const interactive = canEdit && Boolean(onChange);

          return (
            <div key={stage} className={cn('flex items-start', isLast ? 'flex-none' : 'flex-1')}>
              <div className="flex flex-col items-center gap-1.5 min-w-[72px]">
                <button
                  type="button"
                  disabled={!interactive}
                  onClick={() => onChange?.(stage)}
                  className={cn(
                    'relative z-10 h-7 w-7 rounded-full flex items-center justify-center text-xs font-semibold transition-colors shrink-0',
                    isComplete || isActive
                      ? 'bg-threadheads-purple text-white'
                      : 'border-2 border-muted-foreground/30 bg-background text-muted-foreground',
                    interactive && 'cursor-pointer hover:opacity-80',
                    !interactive && 'cursor-default',
                  )}
                  aria-current={isActive ? 'step' : undefined}
                  aria-label={t('stageStrip.stepAria', {
                    number: idx + 1,
                    label: t(`stage.${stage}`),
                  })}
                >
                  {isComplete ? <Check className="h-3.5 w-3.5" /> : idx + 1}
                </button>
                <span
                  className={cn(
                    'text-[11px] text-center font-medium px-1',
                    isActive
                      ? 'text-threadheads-purple font-semibold'
                      : isComplete
                      ? 'text-foreground'
                      : 'text-muted-foreground',
                  )}
                >
                  {t(`stage.${stage}`)}
                </span>
              </div>
              {!isLast && (
                <div
                  className={cn(
                    'flex-1 h-0.5 mt-[14px]',
                    isComplete
                      ? 'bg-threadheads-purple'
                      : 'border-t-2 border-dashed border-muted-foreground/30 h-0',
                  )}
                />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
